import React, { useEffect, useState } from 'react';
import { Loader2, Copy, CheckCircle, Mail, RefreshCw, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  getEmailDomain, addEmailDomain, getEmailSubscribers, sendCreatorBroadcast, getBroadcastStatus,
  getEmailUsage, startEmailAddonCheckout,
  type EmailDomainState, type EmailUsage,
} from '@/services/creatorApi';

// P1: verify your own sending domain (DKIM). P2: email your subscribers from it,
// with a compliant unsubscribe + mailing address on every send.
export default function CreatorEmailSetup() {
  const { toast } = useToast();
  const [state, setState] = useState<EmailDomainState | null>(null);
  const [loading, setLoading] = useState(true);
  const [domainInput, setDomainInput] = useState('');
  const [busy, setBusy] = useState(false);
  // compose (verified only)
  const [subCount, setSubCount] = useState<number | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [mailingAddr, setMailingAddr] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<{ sent: number; failed: number; total: number } | null>(null);
  const [usage, setUsage] = useState<EmailUsage | null>(null);

  const load = async () => {
    try {
      const s = await getEmailDomain();
      setState(s);
      if (s.mailingAddress) setMailingAddr(s.mailingAddress);
      if (s.status === 'verified') {
        getEmailSubscribers().then(r => setSubCount(r.count)).catch(() => {});
        getEmailUsage().then(setUsage).catch(() => {});
      }
    } catch { /* leave null */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const start = async () => {
    const d = domainInput.trim();
    if (!d) return;
    setBusy(true);
    try { setState(await addEmailDomain(d)); toast({ title: 'Domain added', description: 'Add the DNS records below, then re-check.' }); }
    catch (e: any) { toast({ title: 'Could not add domain', description: e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  const recheck = async () => {
    setBusy(true);
    try {
      const s = await getEmailDomain(); setState(s);
      if (s.status === 'verified') {
        getEmailSubscribers().then(r => setSubCount(r.count)).catch(() => {});
        getEmailUsage().then(setUsage).catch(() => {});
      }
      toast({ title: s.status === 'verified' ? 'Verified!' : 'Still pending', description: s.status === 'verified' ? 'You can email your subscribers now.' : 'DNS can take a few minutes to propagate.' });
    } catch { /* noop */ } finally { setBusy(false); }
  };
  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast({ title: 'Add a subject and message', variant: 'destructive' }); return; }
    if (!mailingAddr.trim()) { toast({ title: 'A mailing address is required', description: 'Anti-spam law requires a physical address in every email.', variant: 'destructive' }); return; }
    if (!window.confirm(`Send to ${subCount ?? 'your'} subscriber${subCount === 1 ? '' : 's'}?`)) return;
    setSending(true); setProgress(null);
    try {
      const r = await sendCreatorBroadcast({ subject: subject.trim(), body: body.trim(), mailingAddress: mailingAddr.trim() });
      toast({ title: 'Sending…', description: `Delivering to ${r.total} subscriber${r.total === 1 ? '' : 's'}.` });
      setSubject(''); setBody('');
      const poll = async () => {
        try {
          const s = await getBroadcastStatus(r.broadcastId);
          setProgress({ sent: s.sent, failed: s.failed, total: s.total });
          if (s.status === 'done') {
            setSending(false);
            toast({ title: 'Sent', description: `Delivered to ${s.sent}${s.failed ? `, ${s.failed} failed` : ''}.` });
            return;
          }
        } catch { /* keep polling */ }
        setTimeout(poll, 1500);
      };
      poll();
    } catch (e: any) { toast({ title: 'Send failed', description: e.message, variant: 'destructive' }); setSending(false); }
  };
  const activate = async () => {
    try { window.location.href = await startEmailAddonCheckout(); }
    catch (e: any) { toast({ title: 'Could not start checkout', description: e.message, variant: 'destructive' }); }
  };
  const copy = (v: string) => { navigator.clipboard.writeText(v); toast({ title: 'Copied' }); };

  if (loading) return null;
  const addonBlocked = !!(usage && usage.enforced && !usage.addonActive);

  const card: React.CSSProperties = { margin: '0 0 20px', padding: '16px 18px', borderRadius: 12, border: '1px solid var(--hj-line, #E8E2D2)', background: 'var(--hj-surface, #fff)', color: 'var(--hj-beige-ink)' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--hj-line, #E8E2D2)', fontSize: 14 };
  const status = state?.status || 'none';

  return (
    <div style={card}>
      <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
        <Mail className="w-4 h-4" />
        <strong style={{ fontSize: 15, color: 'var(--hj-beige-ink)' }}>Email your subscribers</strong>
        {status === 'verified' && <span className="flex items-center gap-1" style={{ fontSize: 12, color: '#1a7f37', fontWeight: 700 }}><CheckCircle className="w-3.5 h-3.5" /> Verified</span>}
        {status === 'pending' && <span style={{ fontSize: 12, color: '#9a6700', fontWeight: 700 }}>Pending DNS</span>}
      </div>

      {status === 'none' && (
        <>
          <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--hj-beige-ink-2)', margin: '0 0 10px' }}>
            Verify your own sending domain so emails come from you (e.g. <code>email.yourdomain.com</code>) — your email reputation stays on your domain.
          </p>
          <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
            <input value={domainInput} onChange={e => setDomainInput(e.target.value)} placeholder="email.yourdomain.com" style={{ ...inputStyle, flex: 1, minWidth: 200 }} />
            <button type="button" onClick={start} disabled={busy || !domainInput.trim()}
              style={{ padding: '9px 18px', borderRadius: 8, background: '#0A0A0A', color: 'var(--hj-yellow-ink)', fontWeight: 700, fontSize: 14, border: 'none', opacity: busy ? 0.6 : 1 }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start verification'}
            </button>
          </div>
        </>
      )}

      {(status === 'pending' || status === 'failed') && state?.domain && (
        <>
          <p style={{ fontSize: 13, color: 'var(--hj-beige-ink-2)', margin: '0 0 10px' }}>
            Domain: <strong style={{ color: 'var(--hj-beige-ink)' }}>{state.domain}</strong> — add these DNS records at your host, then re-check:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {state.records.map((r, i) => (
              <div key={i} style={{ padding: '8px 10px', borderRadius: 8, background: 'var(--hj-beige-deep)', fontSize: 12 }}>
                <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700 }}>{r.type}</span>
                  {r.required ? <span style={{ fontSize: 10, color: '#9a6700', fontWeight: 700 }}>REQUIRED</span> : <span style={{ fontSize: 10, color: 'var(--hj-beige-ink-3)' }}>recommended</span>}
                </div>
                <div className="flex items-center gap-1" style={{ marginTop: 2 }}>
                  <code style={{ wordBreak: 'break-all' }}>{r.host}</code>
                  <button type="button" onClick={() => copy(r.host)} title="Copy host"><Copy className="w-3 h-3" /></button>
                </div>
                <div className="flex items-center gap-1">
                  <span style={{ color: 'var(--hj-beige-ink-3)' }}>→</span>
                  <code style={{ wordBreak: 'break-all' }}>{r.value}</code>
                  <button type="button" onClick={() => copy(r.value)} title="Copy value"><Copy className="w-3 h-3" /></button>
                </div>
                {r.note && <div style={{ color: 'var(--hj-beige-ink-2)', marginTop: 2 }}>{r.note}</div>}
              </div>
            ))}
          </div>
          <button type="button" onClick={recheck} disabled={busy}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--hj-line,#E8E2D2)', background: 'transparent', fontSize: 13, fontWeight: 600 }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} I&rsquo;ve added the records — re-check
          </button>
        </>
      )}

      {status === 'verified' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ fontSize: 13, color: 'var(--hj-beige-ink-2)', margin: 0 }}>
            Sending from <strong style={{ color: 'var(--hj-beige-ink)' }}>{state?.domain}</strong> to{' '}
            <strong style={{ color: 'var(--hj-beige-ink)' }}>{subCount ?? '…'}</strong> subscriber{subCount === 1 ? '' : 's'}.
            {usage && <> · {usage.monthEmails} sent this month.</>}
          </p>
          {addonBlocked && (
            <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--hj-yellow)', color: '#0A0A0A', fontSize: 13 }}>
              <strong>Activate email to send.</strong> Emailing your subscribers is a paid add-on.
              <div style={{ marginTop: 8 }}>
                <button type="button" onClick={activate} style={{ padding: '8px 16px', borderRadius: 8, background: '#0A0A0A', color: 'var(--hj-yellow-ink)', fontWeight: 700, fontSize: 13, border: 'none' }}>Activate email</button>
              </div>
            </div>
          )}
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" style={inputStyle} />
          <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Write your message…" rows={6} style={{ ...inputStyle, resize: 'vertical' }} />
          <input value={mailingAddr} onChange={e => setMailingAddr(e.target.value)} placeholder="Your mailing address (required by law, shown in the footer)" style={inputStyle} />
          <div style={{ fontSize: 11, color: 'var(--hj-beige-ink-2)' }}>Every email includes an unsubscribe link and your mailing address automatically.</div>
          <div>
            <button type="button" onClick={send} disabled={sending || !subCount || addonBlocked}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 8, background: '#0A0A0A', color: 'var(--hj-yellow-ink)', fontWeight: 700, fontSize: 14, border: 'none', opacity: sending ? 0.6 : 1 }}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to subscribers
            </button>
            {sending && progress && (
              <div style={{ fontSize: 12, color: 'var(--hj-beige-ink-2)', marginTop: 6 }}>
                Sent {progress.sent}/{progress.total}{progress.failed ? ` (${progress.failed} failed)` : ''}…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
